import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import MainLayout from '../MainLayout';
import { useNavigate } from 'react-router-dom';

import { fetchAdminDashAnalyticsAll } from '../../../redux/slices/dashSlice';
import { MONTHS, PALETTE, FILTER_BAR_OPTIONS } from './dashDummyData';
import { roleColors, cardColors2, roleOpt, KPI_ACCENTS, chartOptions, buildStats, chartNativeLabelsPlugin } from './dashConstants';

import FilterBar from './FilterBar';
import { KpiCard, ChartCard } from './DashCards';
import DeepAnalysisTable from './DeepAnalysisTable';
import LivePulseFeed from './LivePulseFeed';

import RoleIndicator from '../../utils/RoleIndicator';
import AdminDashDonut from '../../utils/AdminDashDonut';
import AdminDashStats from '../../utils/AdminDashStats';
import DashboardChart from '../../utils/DashboardChart';
import HeatmapChart from '../../utils/HeatmapChart';
import { useDrawer } from '../../../DrawerContext';

import { fetchCategoryOpt, fetchCollectionOpt, fetchSegmentOpt, fetchUserOpt } from '../../../redux/slices/optionSlice';
import { fetchRegion, fetchState } from '../../../redux/slices/clientSlice';

import PinDropIcon from '@mui/icons-material/PinDrop';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import LayersIcon from '@mui/icons-material/Layers';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import PublicIcon from '@mui/icons-material/Public';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import Swal from 'sweetalert2';
import axios from 'axios';
import Apiurl from '../../../Helpers/apiUrl';
import UserService from '../../../Services/UserList';
import ExportDash from './ExportDash';
import Tooltip from './Tooltip';
import { People } from '@mui/icons-material';
import DashDrawer from './DashSideDrawer/DashDrawer';

ChartJS.register(...registerables);
const getInitialFYMonths = () => {
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
};
const DEFAULT_FILTERS = {
    regions: [], states: [], roles: [], collections: [], categories: [],
    prices: [], users: [], brands: [], channels: [],
    years: [String(new Date().getFullYear())],
    months: getInitialFYMonths(),
};

const FILTERS_STORAGE_KEY = 'adminDash_filters';

const loadFiltersFromStorage = () => {
    try {
        const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        return saved ? JSON.parse(saved) : DEFAULT_FILTERS;
    } catch (e) {
        return DEFAULT_FILTERS;
    }
};


const DashboardSkeleton = () => (
    <main className="max-w-[1600px] mx-auto px-6 pt-6 flex flex-col gap-5">
        <div className="flex gap-4 mb-4 overflow-x-auto pb-2">
            {[...Array(6)].map((_, i) => (
                <Skeleton key={i} variant="rounded" width={160} height={100} className="flex-shrink-0 rounded-2xl" />
            ))}
        </div>
        <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={110} className="rounded-2xl" />
            ))}
        </section>
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={300} className="rounded-2xl" />
            ))}
        </section>
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            {[...Array(2)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={400} className="rounded-2xl" />
            ))}
        </section>
    </main>
);

export default function AdminDash() {
    const { drawerVisible } = useDrawer();
    const currentDrawerWidth = drawerVisible ? "240px" : "80px";

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { collectionOpt, categoryOpt, userOpt } = useSelector((s) => s.options);
    const { regionOpt, stateOpt } = useSelector((state) => state.adminOption);
    const { stateData, regionData } = useSelector((s) => s.client);
    const { analyticsAll, analyticsStatus } = useSelector((s) => s.dash);
    const analyticsSec1 = analyticsAll?.v1 || {};
    const analyticsSec2 = analyticsAll?.v2 || {};
    const analyticsSec3 = analyticsAll?.v3 || {};
    const { masterProduct = [] } = useSelector(s => s.product);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerContext, setDrawerContext] = useState(null);
    const [channelOpt, setChannelOpt] = useState([]);

    const isGlobalLoading = analyticsStatus === 'pending' || analyticsStatus === 'idle' || !analyticsAll;

    const [filters, setFilters] = useState(loadFiltersFromStorage);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const dropdownRef = useRef(null);

    const [headerHeight, setHeaderHeight] = useState('90px');
    const headerRef = useRef(null);

    useEffect(() => {
        const updateHeaderHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(`${headerRef.current.offsetHeight}px`);
            }
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    useEffect(() => {
        dispatch(fetchCollectionOpt());
        dispatch(fetchCategoryOpt());
        // dispatch(fetchSegmentOpt());
        dispatch(fetchUserOpt());
        dispatch(fetchRegion());
        UserService.GetChannels().then(response => {
            if (response && response.data) {
                setChannelOpt(response.data.map(ch => ({
                    id: ch.id,
                    name: ch.channel_name
                })));
            }
        }).catch(console.error);
    }, []);


    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const timerId = setTimeout(() => {
            const fmt = (arr) => (arr?.length > 0 ? arr.join(',') : '');
            const selectedRoleIds = filters.roles.map((label) => {
                const match = roleOpt.find((opt) => opt.label === label);
                return match ? match.value : label;
            });

            const payload = {
                regions: fmt(filters.regions),
                states: fmt(filters.states),
                roles: fmt(selectedRoleIds),
                collections: fmt(filters.collections),
                categories: fmt(filters.categories),
                priceSegments: fmt(filters.prices),
                userId: fmt(filters.users),
                brands: fmt(filters.brands),
                years: fmt(filters.years),
                months: fmt(filters.months),
                channel: fmt(filters.channels),
            };
            dispatch(fetchAdminDashAnalyticsAll(payload));
        }, 800);

        return () => clearTimeout(timerId);
    }, [filters, dispatch]);

    useEffect(() => {
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
        } catch (e) {
            // sessionStorage might be unavailable (private mode, quota, etc.) — fail silently
        }
    }, [filters]);

    const dynamicSegmentOptions = useMemo(() => {
        if (!masterProduct || masterProduct.length === 0) return [];

        const uniqueSegments = new Set();
        const selectedBrands = (filters.brands || []).map(b => String(b).toLowerCase());

        masterProduct.forEach((product) => {
            const brand = product.brand?.trim();
            const segment = product.priceSegment?.trim();

            if (!segment) return;

            if (selectedBrands.length > 0) {
                if (brand && selectedBrands.includes(brand.toLowerCase())) {
                    uniqueSegments.add(segment);
                }
            } else {
                uniqueSegments.add(segment);
            }
        });

        return Array.from(uniqueSegments);
    }, [masterProduct, filters.brands]);

    const handleFilterToggle = (key, val) => {
        setFilters((prev) => {
            if (key === 'months') {
                const list = prev.months || [];
                return {
                    ...prev,
                    months: list.some(i => String(i) === String(val))
                        ? list.filter((i) => String(i) !== String(val))
                        : [...list, val]
                };
            }

            // if (key === 'regions') {
            //     const isSelected = prev.regions.some(r => String(r) === String(val));
            //     setActiveDropdown(null);
            //     return { ...prev, regions: isSelected ? [] : [val], states: [] };
            // }

            const list = prev[key] || [];
            const exists = list.some((i) => String(i) === String(val));

            return {
                ...prev,
                [key]: exists
                    ? list.filter((i) => String(i) !== String(val))
                    : [...list, String(val)]
            };
        });
    };

    const resetAllFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setActiveDropdown(null);
        try {
            sessionStorage.removeItem(FILTERS_STORAGE_KEY);
        } catch (e) { }
    };

    const hasActiveFilters =
        (filters.collections?.length > 0) ||
        (filters.categories?.length > 0) ||
        (filters.prices?.length > 0) ||
        (filters.regions?.length > 0) ||
        (filters.states?.length > 0) ||
        (filters.roles?.length > 0) ||
        (filters.users?.length > 0) ||
        (filters.channels?.length > 0) ||
        (filters.brands?.length > 0);


    const chartConfig = useMemo(() => {
        if (!analyticsSec1 || !analyticsSec2 || !analyticsSec3) return null;

        const defaultBarColors = Array(20).fill('#0a2540');
        const barColor = hasActiveFilters ? PALETTE : defaultBarColors;

        const trend = analyticsSec1.monthlyQuotationTrend || {};
        // const trendData = MONTHS.map((m) => trend[m.toLowerCase()] || trend[m] || 0);
        const FY_MONTHS = [
            { label: 'Apr', key: 'apr' },
            { label: 'May', key: 'may' },
            { label: 'Jun', key: 'jun' },
            { label: 'Jul', key: 'jul' },
            { label: 'Aug', key: 'aug' },
            { label: 'Sep', key: 'sep' },
            { label: 'Oct', key: 'oct' },
            { label: 'Nov', key: 'nov' },
            { label: 'Dec', key: 'dec' },
            { label: 'Jan', key: 'jan' },
            { label: 'Feb', key: 'feb' },
            { label: 'Mar', key: 'mar' },
        ];

        const trendData = FY_MONTHS.map(({ key }) => trend[key] ?? 0);

        const cat = analyticsSec2.categoryDistributionOfQuotations || [];
        const price = analyticsSec2.priceSegmentDistributionOfQuotations || [];
        const col = analyticsSec2.topCollectionDistributionOfQuotations || [];
        const reg = analyticsSec3.regionDistributionOfQuotations || [];
        const state = analyticsSec3.stateDistributionOfQuotations || [];
        const user = analyticsSec3.topUserDistributionOfQuotations || [];
        const role = analyticsSec3.roleDistributionOfQuotations || [];

        const statusData = analyticsSec2.statusDistributionOfQuotations || [];
        const cityData = analyticsSec2.top10CitiesOfQuotations || [];
        const clientData = analyticsSec2.top10ClientsOfQuotations || [];
        const clientVsQuoteRaw = analyticsSec1.clientVsQuotations || [];

        return {
            monthly: {
                labels: FY_MONTHS.map(({ label }) => label),
                datasets: [{ label: 'Quotations', data: trendData, borderColor: '#0a2540', backgroundColor: 'rgba(10,37,64,0.07)', fill: true, tension: 0.4, pointBackgroundColor: '#0a2540', pointRadius: 3, borderWidth: 2 }],
            },
            category: {
                labels: cat.map((c) => c.name),
                datasets: [{
                    label: 'Quotations',
                    data: cat.map((c) => c.quotations),
                    backgroundColor: barColor,
                    borderRadius: 6,
                    borderSkipped: false
                }]
            },
            clientVsQuote: {
                labels: FY_MONTHS.map(({ label }) => label),
                datasets: [
                    {
                        label: 'Quotations',
                        data: FY_MONTHS.map(({ label }) =>
                            clientVsQuoteRaw.find((d) => d.month.toLowerCase() === label.toLowerCase())?.quotations ?? 0
                        ),
                        backgroundColor: '#0a2540',
                        borderRadius: 6,
                        borderSkipped: false,
                    },
                    {
                        label: 'Clients',
                        data: FY_MONTHS.map(({ label }) =>
                            clientVsQuoteRaw.find((d) => d.month.toLowerCase() === label.toLowerCase())?.clients ?? 0
                        ),
                        backgroundColor: '#f59e0b',
                        borderRadius: 6,
                        borderSkipped: false,
                    },
                ],
            },
            price: { labels: price.map((p) => p.name), datasets: [{ label: 'Quotations', data: price.map((p) => p.quotations), backgroundColor: hasActiveFilters ? ['#0a2540', '#1a6b5e', '#f59e0b', '#3b82f6'] : defaultBarColors, borderRadius: 6, borderSkipped: false }] },
            region: { labels: reg.map((r) => r.name), datasets: [{ label: 'Quotations', data: reg.map((r) => r.quotations), backgroundColor: barColor, borderRadius: 6, borderSkipped: false }] },
            state: { labels: state.map((s) => s.name), datasets: [{ label: 'Quotations', data: state.map((s) => s.quotations), backgroundColor: barColor, borderRadius: 6, borderSkipped: false }] },
            user: { labels: user.map((u) => u.name), datasets: [{ label: 'Quotations', data: user.map((u) => u.quotations), backgroundColor: barColor, borderRadius: 6, borderSkipped: false }] },
            role: { labels: role.map((ro) => ro.name), datasets: [{ data: role.map((ro) => ro.quotations), backgroundColor: barColor, borderWidth: 0, hoverOffset: 6 }] },
            collection: { labels: col.map((co) => co.name), datasets: [{ label: 'Quotations', data: col.map((co) => co.quotations), backgroundColor: hasActiveFilters ? ['#f59e0b', '#0a2540', '#1a6b5e'] : defaultBarColors, borderRadius: 6, borderSkipped: false }] },
            city: { labels: cityData.map((c) => c.name), datasets: [{ label: 'Quotations', data: cityData.map((c) => c.quotations), backgroundColor: barColor, borderRadius: 6, borderSkipped: false }] },
            client: { labels: clientData.map((c) => c.name), datasets: [{ label: 'Quotations', data: clientData.map((c) => c.quotations), backgroundColor: barColor, borderRadius: 6, borderSkipped: false }] },

        };
    }, [analyticsAll, hasActiveFilters, filters]);

    const kpis = analyticsSec1?.kpis || {};
    const totalLogins = useMemo(() => {
        if (!analyticsSec1?.usersRoleCount) return 0;
        let sum = 0;
        Object.entries(analyticsSec1.usersRoleCount).forEach(([key, val]) => {
            if (key.startsWith('active')) {
                sum += (Number(val) || 0);
            }
        });
        return sum;
    }, [analyticsSec1?.usersRoleCount]);

    const KPI_DEFS = [
        { label: 'Total Quotations', val: kpis.totalQuotations, sub: 'All-time volume', icon: <TrendingUpIcon />, path: '/admin/projects' },
        { label: 'Total Clients', val: kpis.totalClients, sub: 'Total number of clients', icon: <People />, path: '/admin/clientlist' },
        // Replace Accounts Created KPI with Total Logins sum of activeLogins rolewise
        { label: 'Total Logins (selected months)', val: totalLogins, sub: 'Sum of all the login rolewise', icon: <GroupIcon />, path: '/admin/userlist' },
        // { label: 'Regions', val: kpis.regions, sub: 'Coverage zones', icon: <PublicIcon /> },
        // { label: 'States Mapped', val: kpis.states, sub: 'Territory reach', icon: <MapIcon /> },
        // { label: 'Active Roles', val: kpis.roles, sub: 'Hierarchy levels', icon: <BadgeIcon /> },
        { label: 'Collections', val: kpis.collections, sub: 'Unique available collections', icon: <LayersIcon />, path: '/admin/product' },
        { label: "Monthly Login", val: kpis.todaysLoginCount, sub: "User's logged in selected or current month", icon: <CategoryIcon /> },
        // { label: 'Price Segments', val: kpis.priceSegments, sub: 'Unique available segments', icon: <CurrencyRupeeIcon /> },
    ];

    const handleKpiCardClick = (kpiItem) => {
        if (!kpiItem.path) return;

        let passState = {
            roles: filters.roles,
            users: filters.users,
            regions: filters.regions,
            states: filters.states,
            is_active: "1",
        };

        if (kpiItem.label === 'New Clients') {
            passState.years = filters.years;
            passState.months = filters.months;
            passState.filterByClientCreatedAt = true; // Flag for backend to filter by client creation date
        } else {
            passState.years = kpiItem.path.includes('client') || kpiItem.path.includes('user') ? filters.years : filters.years;
            passState.months = filters.months;
            if (kpiItem.label === 'Total Clients') {
                passState.filterByClientCreatedAt = false;
            }
        }

        navigate(kpiItem.path, {
            state: passState
        });
    };

    const FILTER_DEFS = [
        { name: 'Brand', id: 'brands', list: ['GROHE', 'AS'], icon: <LocalOfferIcon /> },
        { name: 'Role', id: 'roles', icon: <BadgeIcon /> },
        { name: 'User', id: 'users', icon: <PersonIcon /> },
        { name: 'Channel', id: 'channels', list: channelOpt, icon: <LayersIcon /> },
        { name: 'Region', id: 'regions', list: regionOpt?.map(r => ({ ...r, id: r.label })) || [], icon: <PinDropIcon /> },
        { name: 'State', id: 'states', list: stateOpt?.map(s => ({ ...s, id: s.label })) || [], icon: <MapIcon /> },
        { name: 'Collection', id: 'collections', list: collectionOpt, icon: <LocalOfferIcon /> },
        { name: 'Category', id: 'categories', list: categoryOpt, icon: <LocalOfferIcon /> },
        { name: 'Segment', id: 'prices', list: dynamicSegmentOptions, icon: <LocalOfferIcon /> },
        {
            name: 'Year', id: 'years', list: [
                { label: "2026-2027", value: "2026" },
                { label: "2025-2026", value: "2025" },
                { label: "2024-2025", value: "2024" },
                { label: "2023-2024", value: "2023" },
            ], icon: <DateRangeIcon />
        },
        // { name: 'From Month', id: 'monthFrom', list: MONTHS.map((m, i) => ({ id: i + 1, name: m })), icon: <DateRangeIcon /> },
        // { name: 'To Month', id: 'monthTo', list: MONTHS.map((m, i) => ({ id: i + 1, name: m })), icon: <DateRangeIcon /> },
    ];

    const mkDonutData = (regionMap, colorSet) => {
        // 1. Convert object entries to array, filter invalid items, and sort descending
        const sortedEntries = Object.entries(regionMap || {})
            .filter(([key]) => Boolean(key))
            .sort(([, valA], [, valB]) => valB - valA);

        const labels = sortedEntries.map(([key]) => key);
        const data = sortedEntries.map(([, val]) => val);

        return {
            labels: labels,
            datasets: [{
                data: data,
                backgroundColor: colorSet,
                hoverOffset: 10,
            }],
        };
    };

    const sortedRegions = analyticsSec3?.regionDistributionOfQuotations || [];

    const regionClientColors = hasActiveFilters
        ? ['#0f3b63', '#0d9488', '#d97706', '#4682b4', '#b91c1c', '#475569', '#15803d', '#b45309']
        : Array(10).fill('#0a2540');

    const regionCleint = mkDonutData(
        analyticsSec1?.clientsByRegion,
        regionClientColors
    );

    const statusDonutColors = hasActiveFilters
        ? ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#6366f1', '#0f3b63']
        : Array(10).fill('#0a2540');

    // Convert array of {name, quotations} to object for mkDonutData
    const statusMap = (analyticsSec2?.statusDistributionOfQuotations || []).reduce((acc, curr) => {
        acc[curr.name] = curr.quotations;
        return acc;
    }, {});

    const statusDonut = mkDonutData(statusMap, statusDonutColors);

    const mkStacked = (rows, getLabel, getRoleMap) => {
        const allRoles = new Set();
        (rows || []).forEach(row => {
            Object.keys(getRoleMap(row) || {}).forEach(role => allRoles.add(role));
        });

        return {
            labels: (rows || []).map(getLabel),
            datasets: Array.from(allRoles).map((role) => {
                const normalizedRole = role.replace(/\s+/g, '');
                const roleCountKey = 'total' + normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1).toLowerCase();
                const userCount = analyticsSec1?.usersRoleCount?.[roleCountKey] || analyticsSec1?.usersRoleCount?.['total' + normalizedRole] || 0;

                return {
                    label: role.toUpperCase() + (userCount > 0 ? ` (${userCount})` : ''),
                    data: (rows || []).map((item) => {
                        const dist = getRoleMap(item)[role];
                        let val = 0;
                        let cellUserCount = 0;

                        if (typeof dist === 'object' && dist !== null) {
                            val = dist.count !== undefined ? dist.count : (dist.val !== undefined ? dist.val : 0);
                            cellUserCount = dist.userCount !== undefined ? dist.userCount : 0;
                        } else {
                            val = typeof dist === 'number' || typeof dist === 'string' ? Number(dist) : 0;
                        }

                        return {
                            val: isNaN(val) ? 0 : val,
                            userCount: isNaN(cellUserCount) ? 0 : cellUserCount
                        };
                    }),
                    backgroundColor: roleColors[role] || '#94a3b8',
                    stack: 'combined',
                };
            }),
        };
    };

    const monthlyLogins = mkStacked(analyticsSec2?.monthlyLogins, (r) => r.month, (r) => r?.roleDistribution || {});
    const monthlyUserQuotes = mkStacked(analyticsSec2?.monthlyRoleQuotes, (r) => r.month, (r) => r?.roleDistribution || {});
    const stateQuotes = mkStacked(analyticsSec2?.quotesByState, (r) => r.state, (r) => r?.roleDistribution || {});

    const downloadFile = async (excelType = 'sku') => {
        try {
            const fmt = (arr) => (arr?.length > 0 ? arr.join(',') : '');

            const payload = {
                type: excelType,
                regions: fmt(filters.regions),
                states: fmt(filters.states),
                roles: fmt(filters.roles),
                collection: fmt(filters.collections),
                categories: fmt(filters.categories),
                priceSegments: fmt(filters.prices),
                emp_codes: fmt(filters.users),
                brands: fmt(filters.brands),
                years: fmt(filters.years),
                months: fmt(filters.months)
            };
            const token = localStorage.getItem('u_token');
            const response = await axios.get(`${Apiurl}admin/dashboardExcel`, {
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                params: payload,
                responseType: 'blob',
            });

            const cd = response.headers['content-disposition'];
            let filename = 'export.xlsx';
            if (cd) { const m = cd.match(/filename="?(.+)"?/); if (m?.[1]) filename = m[1]; }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
            const a = Object.assign(document.createElement('a'), { href: url, download: filename });
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Swal.fire({ icon: 'warning', title: 'Download Failed', text: error.message || 'Something went wrong.', timer: 3000 });
        }
    };

    const filteredUserOptions = useMemo(() => {
        if (!filters.roles || filters.roles.length === 0) {
            return (userOpt || []).map(user => ({
                ...user,
                name: `${user.name || ''} ${user.last_name || ''}`.trim()
            }));
        }

        const selectedRoleNames = filters.roles.map(r => String(r).toLowerCase());

        const selectedRoleIds = filters.roles.map((name) => {
            const match = roleOpt?.find((opt) => opt.label === name || opt.value === name);
            return match ? String(match.value).toLowerCase() : String(name).toLowerCase();
        });

        return (userOpt || [])
            .filter((user) => {
                if (!user || !user.role_id) return false;

                const userRoleIdStr = String(user.role_id).toLowerCase();
                const matchesId = selectedRoleIds.includes(userRoleIdStr);
                const matchesName = selectedRoleNames.includes(userRoleIdStr);

                return matchesId || matchesName;
            })
            .map((user) => ({
                ...user,
                name: `${user.name || ''} ${user.last_name || ''}`.trim()
            }));
    }, [filters.roles, userOpt, roleOpt]);


    const MONTH_INDEX_MAP = {
        'january': 1, 'february': 2, 'march': 3, 'april': 4, 'may': 5, 'june': 6,
        'july': 7, 'august': 8, 'september': 9, 'october': 10, 'november': 11, 'december': 12,
        'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4, 'jun': 6, 'jul': 7, 'aug': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12
    };

    const handleMonthlyTrendClick = (monthLabel) => {
        const cleanLabel = String(monthLabel).trim().toLowerCase();
        const numericMonth = MONTH_INDEX_MAP[cleanLabel] || (new Date().getMonth() + 1);

        const displayMonth = cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1);

        const drawerContextPayload = {
            contextType: 'monthlyTrend',
            title: `Monthly Trend Distribution of (${displayMonth})`,
            subtitle: ``,
            selectedMonth: String(numericMonth),
            displayMonth: displayMonth
        };

        sessionStorage.setItem('dash_drawer_context', JSON.stringify(drawerContextPayload));
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        sessionStorage.removeItem('dash_drawer_context');
        setDrawerOpen(false);
    };

    const monthlyTrendOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const monthLabel = chart.data.labels[elementIndex];
                handleMonthlyTrendClick(monthLabel);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleRegionComparisonClick = (regionClicked) => {
        if (!regionClicked) return;

        navigate('/admin/projects', {
            state: {
                years: filters.years,
                users: filters.users,
                roles: filters.roles,
                regions: [regionClicked],
                states: filters.states,
                months: filters.months
            }
        });
    };

    const regionComparisonOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const regionClicked = chart.data.labels[elementIndex];
                handleRegionComparisonClick(regionClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleTopStateClick = (stateClicked) => {
        if (!stateClicked) return;

        navigate('/admin/projects', {
            state: {
                years: filters.years,
                users: filters.users,
                roles: filters.roles,
                regions: filters.regions,
                states: [stateClicked],
                months: filters.months
            }
        });
    };

    const topStateOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const stateClicked = chart.data.labels[elementIndex];
                handleTopStateClick(stateClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const topCityOptions = {
        ...chartOptions(true, false, true),
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const topClientOptions = {
        ...chartOptions(true, false, true),
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleRegionClientClick = (regionClicked) => {
        if (!regionClicked) return;

        navigate('/admin/clientlist', {
            state: {
                years: filters.years,
                months: filters.months,
                users: filters.users,
                roles: filters.roles,
                regions: [regionClicked],
                states: filters.states
            }
        });
    };

    const regionClientOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const regionClicked = chart.data.labels[elementIndex];
                handleRegionClientClick(regionClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleCategoryMixClick = (categoryClicked) => {
        if (!categoryClicked) return;

        navigate('/admin/product-log', {
            state: {
                users: filters.users,
                regions: filters.regions,
                states: filters.states,
                roles: filters.roles,
                category: categoryClicked,
                collection: filters.collections,
                priceSegment: filters.prices,
                years: filters.years,
                month: filters.months
            }
        });
    };

    const categoryMixOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const categoryClicked = chart.data.labels[elementIndex];
                handleCategoryMixClick(categoryClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handlePriceSegmentClick = (segmentClicked) => {
        if (!segmentClicked) return;

        navigate('/admin/product-log', {
            state: {
                years: filters.years,
                users: filters.users,
                regions: filters.regions,
                states: filters.states,
                roles: filters.roles,
                category: null,
                collection: filters.collections,
                priceSegment: [segmentClicked],
                month: filters.months
            }
        });
    };

    const priceSegmentOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const segmentClicked = chart.data.labels[elementIndex];
                handlePriceSegmentClick(segmentClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };
    const handleCollectionClick = (collectionClicked) => {
        if (!collectionClicked) return;

        navigate('/admin/product-log', {
            state: {
                years: filters.years,
                users: filters.users,
                regions: filters.regions,
                states: filters.states,
                roles: filters.roles,
                category: null,
                priceSegment: filters.prices,
                collection: [collectionClicked],
                month: filters.months
            }
        });
    };

    const collectionOptions = {
        ...chartOptions(true, false, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const collectionClicked = chart.data.labels[elementIndex];
                handleCollectionClick(collectionClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleUserLeaderboardClick = (userNameClicked) => {
        if (!userNameClicked) return;

        const matchedUser = userOpt?.find(
            (u) => `${u.name || ''} ${u.last_name || ''}`.trim() === userNameClicked.trim()
        );
        navigate('/admin/projects', {
            state: {
                years: filters.years,
                users: [matchedUser?.emp_code],
                roles: filters.roles,
                regions: filters.regions,
                states: filters.states,
                months: filters.months
            }
        });
    };

    const userLeaderboardOptions = {
        ...chartOptions(true, true, true),
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const elementIndex = elements[0].index;
                const userNameClicked = chart.data.labels[elementIndex];
                handleUserLeaderboardClick(userNameClicked);
            }
        },
        onHover: (event, elements, chart) => {
            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
    };

    const handleRoleDistributionClick = (roleLabelClicked) => {
        if (!roleLabelClicked) return;

        const match = roleOpt.find((opt) => opt.label.toLowerCase() === roleLabelClicked.toLowerCase());
        const targetRole = match ? match.value : roleLabelClicked;

        navigate('/admin/userlist', {
            state: {
                years: filters.years,
                months: filters.months,
                regions: filters.regions,
                states: filters.states,
                role: targetRole,
            }
        });
    };

    const roleDistributionOptions = chartOptions(false);

    roleDistributionOptions.onClick = (event, elements, chart) => {
        if (elements.length > 0) {
            const elementIndex = elements[0].index;
            const roleLabelClicked = chart.data.labels[elementIndex];
            handleRoleDistributionClick(roleLabelClicked);
        }
    };

    roleDistributionOptions.onHover = (event, elements, chart) => {
        chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    };

    const handleStackedMonthlyClick = (targetPath, monthLabel, roleLabel) => {
        const cleanLabel = String(monthLabel).trim().toLowerCase().substring(0, 3);
        const monthIndex = MONTHS.findIndex((m) =>
            String(m).trim().toLowerCase().substring(0, 3) === cleanLabel
        );
        const monthNumeric = monthIndex !== -1 ? monthIndex + 1 : (new Date().getMonth() + 1);

        const match = roleOpt.find((opt) => opt.label.toLowerCase() === roleLabel.toLowerCase());
        const targetRole = match ? match.value : roleLabel;

        navigate(targetPath, {
            state: {
                years: filters.years,
                users: filters.users,
                regions: filters.regions,
                states: filters.states,
                roles: filters.roles,
                category: [],
                collection: filters.collections,
                priceSegment: filters.prices,
                month: [monthNumeric]
            }
        });
    };

    const handleStackedStateClick = (stateLabel, roleLabel) => {
        const match = roleOpt.find((opt) => opt.label.toLowerCase() === roleLabel.toLowerCase());
        const targetRole = match ? match.value : roleLabel;

        navigate('/admin/projects', {
            state: {
                years: filters.years,
                users: filters.users,
                roles: filters.roles,
                regions: filters.regions,
                states: [stateLabel],
                months: filters.months
            }
        });
    };

    const staticBarOptions = chartOptions(true, false, true);

    return (
        <MainLayout title="Dashboard" topbar={false} disableMenu={false}>
            <DashDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                contextType={drawerContext}
                filters={filters}
            />
            <div className="min-h-screen antialiased pb-16" style={{ background: '#f4f6f9', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <div
                    ref={headerRef}
                    className="fixed top-0 bg-[#f4f6f9] pt-2 pb-2 shadow-sm border-b border-slate-200/50 z-[9999]"
                    style={{
                        width: `calc(100% - ${currentDrawerWidth})`,
                        left: currentDrawerWidth,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                >
                    <FilterBar
                        filterDefs={FILTER_DEFS}
                        filters={filters}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        onToggle={handleFilterToggle}
                        onReset={resetAllFilters}
                        isGlobalLoading={isGlobalLoading}
                        userOpt={filteredUserOptions}
                        filterBarOptions={FILTER_BAR_OPTIONS}
                        dropdownRef={dropdownRef}
                    />
                </div>
                <div style={{ height: headerHeight, transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }} />
                {isGlobalLoading ? <DashboardSkeleton /> : (
                    <main className="max-w-[1600px] mx-auto px-6 pt-5 flex flex-col gap-5">

                        <RoleIndicator
                            data={analyticsSec1?.usersRoleCount || {}}
                            roles={filters.roles}
                            onRoleClick={handleRoleDistributionClick}
                        />

                        <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                            {KPI_DEFS.map((k, i) => (
                                <div
                                    key={i}
                                    onClick={() => handleKpiCardClick(k)}
                                    className="cursor-pointer transform transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99]"
                                >
                                    <KpiCard key={i} label={k.label} val={k.val} sub={k.sub} icon={k.icon} accent={KPI_ACCENTS[i]} />
                                </div>
                            ))}
                        </section>

                        <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                            <div className="xl:col-span-3">
                                <DeepAnalysisTable analyticsSec1={analyticsSec1} analyticsSec2={analyticsSec2} analyticsSec3={analyticsSec3} filters={filters} />
                            </div>
                            <div className="xl:col-span-1">
                                <LivePulseFeed />
                            </div>
                        </section>

                        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>Region Comparison</span>
                                            <span className='text-[8px]'>(Quotations made by region)</span>
                                        </span>

                                    </div>
                                }
                                icon={<PublicIcon sx={{ fontSize: 14 }} />}
                            >
                                {chartConfig && <div className="h-56"><Bar data={chartConfig.region} options={regionComparisonOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                            </ChartCard>
                            <AdminDashStats stats={buildStats(analyticsSec1)} cardColors2={cardColors2} />



                        </section>
                        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>Category Mix</span>
                                            <span className='text-[8px]'>(Quotations made by category)</span>
                                        </span>
                                    </div>
                                }
                                icon={<CategoryIcon sx={{ fontSize: 14 }} />}>
                                {chartConfig && (
                                    <div className="h-52">
                                        <Bar
                                            data={chartConfig.category}
                                            options={categoryMixOptions}
                                            plugins={[chartNativeLabelsPlugin]}
                                        />
                                    </div>
                                )}
                            </ChartCard>
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>Price Segments</span>
                                            <span className='text-[8px]'>(Quotations made by price segment)</span>
                                        </span>


                                    </div>
                                }
                                icon={<CurrencyRupeeIcon sx={{ fontSize: 14 }} />}
                            >
                                {chartConfig && <div className="h-52"><Bar data={chartConfig.price} options={priceSegmentOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                            </ChartCard>
                        </section>
                        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>Top 10 States</span>
                                            <span className='text-[8px]'>(Quotations by state)</span>
                                        </span>

                                    </div>
                                }
                                icon={<MapIcon sx={{ fontSize: 14 }} />}
                            >
                                {chartConfig && <div className="h-56"><Bar data={chartConfig.state} options={topStateOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                            </ChartCard>
                            <AdminDashDonut regionCleint={regionCleint} onClick={handleRegionClientClick} options={regionClientOptions} />
                        </section>

                        <ChartCard
                            title={
                                <div className="flex items-center justify-between w-full gap-2">
                                    <span className="flex flex-col">
                                        <span>Monthly Trend</span>
                                        <span className='text-[8px]'>(Quotations made by month)</span>
                                    </span>
                                </div>
                            }
                            icon={<EqualizerIcon sx={{ fontSize: 14 }} />}
                        >
                            {chartConfig && <div className="h-52"><Line data={chartConfig.monthly} options={monthlyTrendOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                        </ChartCard>

                        {analyticsSec1.clientVsQuotations &&
                            <section className="grid grid-cols-1 gap-4">
                                <ChartCard
                                    title={
                                        <div className="flex items-center justify-between w-full gap-2">
                                            <span className="flex flex-col">
                                                <span>Clients vs Quotations</span>
                                                <span className="text-[8px]">(Monthly breakdown)</span>
                                            </span>
                                        </div>
                                    }
                                    icon={<EqualizerIcon sx={{ fontSize: 14 }} />}
                                >
                                    {chartConfig && (
                                        <div className="h-52">
                                            <Bar
                                                data={chartConfig.clientVsQuote}
                                                options={staticBarOptions}
                                                plugins={[chartNativeLabelsPlugin]}
                                            />
                                        </div>
                                    )}
                                </ChartCard>
                            </section>
                        }

                        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>User Leaderboard</span>
                                            <span className='text-[8px]'>(Top users by quotations)</span>
                                        </span>

                                    </div>
                                }
                                icon={<PersonIcon sx={{ fontSize: 14 }} />}
                            >
                                {chartConfig && <div className="h-52"><Bar data={chartConfig.user} options={userLeaderboardOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                            </ChartCard>
                            {/* <ChartCard
                            title={
                                <div className="flex items-center justify-between w-full gap-2">
                                    <span>Role Distribution</span>
                                    <ExportDash type="roleDistribution" filters={filters} tooltip="Export Role Distribution" variant="icon" />
                                    <Tooltip content="Users distribution by roles" />
                                </div>
                            }
                            icon={<BadgeIcon sx={{ fontSize: 14 }} />}
                        >
                            {chartConfig && <div className="h-52 flex items-center justify-center"><Pie data={chartConfig.role} options={roleDistributionOptions} /></div>}
                        </ChartCard> */}
                            <ChartCard
                                title={
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <span className="flex flex-col">
                                            <span>Collection Breakdown</span>
                                            <span className='text-[8px]'>(Quotations made by collection)</span>
                                        </span>

                                    </div>
                                }
                                icon={<LayersIcon sx={{ fontSize: 14 }} />}
                            >
                                {chartConfig && <div className="h-52"><Bar data={chartConfig.collection} options={collectionOptions} plugins={[chartNativeLabelsPlugin]} /></div>}
                            </ChartCard>
                        </section>

                        <section className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(10,37,64,0.06)' }}>
                            <HeatmapChart title="Quotation Trends" tooltip="Number of quotations made per month by roles" data={monthlyUserQuotes} loading={!monthlyUserQuotes} onClick={(month, role) => handleStackedMonthlyClick('/admin/product-log', month, role)} stretch showColTotals />
                        </section>

                        <section className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(10,37,64,0.06)' }}>
                            <HeatmapChart title="State Wise Quotation" tooltip="Number of quotations made per state by roles" data={stateQuotes} onDownload={() => downloadFile('stateQuotaion')} loading={!stateQuotes} onClick={(stateName, role) => handleStackedStateClick(stateName, role)} showColTotals />
                        </section>

                        <section className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(10,37,64,0.06)' }}>
                            <HeatmapChart title="Monthly User's Login" tooltip="Number of times users logged in per month by roles" data={monthlyLogins} loading={!monthlyLogins} onClick={(month, role) => handleStackedMonthlyClick('/admin/log/details', month, role)} stretch showColTotals />
                        </section>


                    </main>
                )}
            </div>
        </MainLayout>
    );
}